import { NavLink } from 'react-router-dom';
import { ADMIN_ROUTES, PERMISSIONS } from '../../lib/constants';
import { BrandLogo } from '../BrandLogo';

interface AdminSidebarProps {
  permissions: string[];
  onHide: () => void;
}

interface NavItem {
  to: string;
  label: string;
  icon: JSX.Element;
  visible: boolean;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

function UsersIcon(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function BuildingIcon(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4" y="3" width="16" height="18" rx="1.5" />
      <path d="M9 7h.01M15 7h.01M9 11h.01M15 11h.01M9 15h.01M15 15h.01" />
      <path d="M10 21v-3h4v3" />
    </svg>
  );
}

function UserCircleIcon(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="10" r="3" />
      <path d="M6.5 18.5a6 6 0 0 1 11 0" />
    </svg>
  );
}

export function AdminSidebar({ permissions, onHide }: AdminSidebarProps): JSX.Element {
  const sections: NavSection[] = [
    {
      label: 'Management',
      items: [
        {
          to: ADMIN_ROUTES.USERS,
          label: 'Users',
          icon: <UsersIcon />,
          visible: permissions.includes(PERMISSIONS.MANAGE_USERS),
        },
        {
          to: ADMIN_ROUTES.INSTANCES,
          label: 'Instances',
          icon: <BuildingIcon />,
          visible: permissions.includes(PERMISSIONS.MANAGE_FACILITIES),
        },
      ],
    },
    {
      label: 'Account',
      items: [
        {
          to: ADMIN_ROUTES.ACCOUNT,
          label: 'My account',
          icon: <UserCircleIcon />,
          visible: true,
        },
      ],
    },
  ];

  const visibleSections = sections
    .map((section) => ({ ...section, items: section.items.filter((item) => item.visible) }))
    .filter((section) => section.items.length > 0);

  return (
    <aside className="admin-sidebar">
      <div className="admin-sidebar__brand">
        <BrandLogo small />
        <button
          type="button"
          className="admin-sidebar__hide"
          aria-label="Hide sidebar"
          onClick={onHide}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
      </div>
      <nav className="admin-sidebar__sections">
        {visibleSections.map((section) => (
          <div key={section.label} className="admin-sidebar__section">
            <p className="admin-sidebar__subheading">{section.label}</p>
            <div className="admin-sidebar__nav">
              {section.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `admin-sidebar__item${isActive ? ' admin-sidebar__item--active' : ''}`
                  }
                >
                  <span className="admin-sidebar__icon">{item.icon}</span>
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>
    </aside>
  );
}
